import React, { useCallback } from 'react'
import Editor from 'react-simple-code-editor'
import type { Block } from '../types'
import { highlightCodeToHtml } from '../../lib/codeHighlight'
import { Copy, Check } from 'lucide-react'

interface InlineCodeEditorProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

type CodeFrame = 'default' | 'terminal' | 'browser'

export default function InlineCodeEditor({ block, onUpdateBlock }: InlineCodeEditorProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const code = (config.code as string) || ''
  const language = (config.language as string) || 'javascript'
  const frame = (config.frame as CodeFrame) || 'default'
  const filename = (config.filename as string) || ''

  const [copied, setCopied] = React.useState(false)

  const handleCodeChange = useCallback((newCode: string) => {
    onUpdateBlock(block.id, { config: { ...config, code: newCode } })
  }, [block.id, config, onUpdateBlock])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [code])

  const highlight = useCallback((code: string) => highlightCodeToHtml(code, language), [language])

  const copyButton = (
    <button
      onClick={(e) => { e.stopPropagation(); handleCopy() }}
      className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )

  const editorElement = (
    <div
      data-inline-editor
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Editor
        value={code}
        onValueChange={handleCodeChange}
        highlight={highlight}
        padding={16}
        className="font-mono text-sm text-slate-200 leading-relaxed min-h-[60px] outline-none"
        style={{ whiteSpace: 'pre', overflowX: 'auto' }}
        textareaClassName="outline-none"
        placeholder="// Type your code here..."
      />
    </div>
  )

  if (frame === 'terminal') {
    return (
      <div className="rounded-xl overflow-hidden shadow-2xl">
        <div className="bg-[#1e1e1e] px-4 py-2.5 flex items-center border-b border-[#333]">
          <div className="flex gap-2">
            <div className="size-3 rounded-full bg-[#ff5f57]" />
            <div className="size-3 rounded-full bg-[#febc2e]" />
            <div className="size-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 text-center">
            <span className="text-[11px] text-slate-400 font-mono">{filename || `${language} — Terminal`}</span>
          </div>
          {copyButton}
        </div>
        <div className="bg-[#1a1a2e]">
          <div className="px-4 pt-3 pb-0">
            <span className="text-green-400">$</span>
            <span className="text-slate-500 text-xs ml-2">~/{filename || 'code'}</span>
          </div>
          {editorElement}
        </div>
      </div>
    )
  }

  if (frame === 'browser') {
    return (
      <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200">
        <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-3 border-b border-slate-200">
          <div className="flex gap-1.5">
            <div className="size-3 rounded-full bg-red-400" />
            <div className="size-3 rounded-full bg-amber-400" />
            <div className="size-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-mono truncate border border-slate-200">
            {filename || `index.${language === 'html' ? 'html' : language === 'css' ? 'css' : 'js'}`}
          </div>
          {copyButton}
        </div>
        <div className="bg-slate-900">
          {editorElement}
        </div>
      </div>
    )
  }

  return (
    <div className="relative rounded-xl overflow-hidden bg-slate-900">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-b border-slate-700">
        <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{filename || language}</span>
        {copyButton}
      </div>
      {editorElement}
    </div>
  )
}
